// Angular
import { AsyncPipe, CurrencyPipe, CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
// Rxjs
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// PrimeNG
import { CheckboxModule } from 'primeng/checkbox';
// Models
import {
  IShoppingCart,
  IShoppingCartProduct,
} from '@core/models-v2/cart/shopping-cart.interface';
import { ICustomerAddress } from '@core/models-v2/customer/customer.interface';
// Pipes
import { ShippingDatePipe } from '@shared/pipes-v2/shipping-date.pipe';
// Services
import { CartService } from '@core/services-v2/cart.service';
import { RootService } from '@shared/services/root.service';
import { CheckoutStorageService } from '../../services/checkout-storage/checkout-storage.service';
import { Store } from '@core/services-v2/geolocation/models/store.interface';
import { ButtonModule } from 'primeng/button';
import { environment } from '@env/environment';
import { SessionService } from '@core/services-v2/session/session.service';

const NG_MODULES = [
  CurrencyPipe,
  AsyncPipe,
  RouterModule,
  CommonModule,
  ButtonModule,
];
const PRIME_MODULES = [CheckboxModule];
const PIPES = [ShippingDatePipe];

@Component({
  selector: 'app-summary-order',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, PIPES],
  templateUrl: './summary-order.component.html',
  styleUrl: './summary-order.component.scss',
  host: {
    class:
      'col-12 lg:col-6 px-4 py-8 md:px-6 lg:px-8 surface-50 hidden lg:block',
  },
})
export class SummaryOrderComponent implements OnInit {
  public countryCode: string = environment.country;

  //#region Inputs
  @Input({ required: true }) step: number = 1;
  @Input({ required: true }) submitButtonLabel: string = '';
  @Input({ required: true }) backButtonLabel: string = '';
  @Input({ required: true }) isLoadingQuotation: boolean = false;
  //#endregion

  @Output() onSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Output() onGoBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() comfirmGenerateQuotation: EventEmitter<MouseEvent> =
    new EventEmitter<MouseEvent>();

  //#region Dependencies
  public readonly cartService = inject(CartService);
  public readonly root = inject(RootService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  private readonly sessionService = inject(SessionService);
  //#endregion

  //#region Properties
  shippingType: string = 'retiro';
  productsAvailable: IShoppingCartProduct[] = [];
  productsUnavailable: IShoppingCartProduct[] = [];
  shippingTypeTitle: string = '';
  cartSession: IShoppingCart | null = null;
  direccionDespacho: ICustomerAddress | null = null;
  store: Store | null = null;
  //#endregion

  isQuotationVisible = environment.visibility.quotation;
  isLoggedIn = signal<boolean>(this.sessionService.isLoggedIn());

  ngOnInit(): void {
    this.shippingTypeTitle = this.getShippingTypeTitle(this.shippingType);
    this.onCartChange();
    this.onShippingTypeChange();
    this.onShoppingCartProductsChange();
  }

  private getShippingTypeTitle(shippingType: string): string {
    if (['retiro', 'TIENDA'].includes(shippingType)) {
      return 'Retiro en tienda';
    } else if (['despacho', 'STD'].includes(shippingType)) {
      return 'Entrega a domicilio';
    } else {
      return 'Entrega a domicilio';
    }
  }

  getProductTotal(product: any): number {
    if (!product) return 0;

    if (this.countryCode === 'es') {
      const qty = product.quantity || 0;
      const iva = product.iva ?? 0.21;
      const netPrice = (product.price || 0) / (1 + iva);

      const precioTruncado = Math.round(netPrice * 100) / 100;
      const importeNetoProducto = Math.round(precioTruncado * qty * 100) / 100;
      const taxAmount = Math.round(importeNetoProducto * iva * 100) / 100;

      return importeNetoProducto + taxAmount;
    }

    return (product.price || 0) * (product.quantity || 0);
  }

  private onCartChange(): void {
    this.cartService.cartDataSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cart) => {
        this.cartSession = cart;
        this.setShippingDetail();
      });
  }

  private setShippingDetail(): void {
    if (!this.cartSession?.shipment) return;
    const shippingType = this.cartSession.shipment.deliveryMode;
    if (shippingType === 'pickup') {
      this.setStoreDetail();
    } else {
      this.setDeliveryAddress();
    }
  }

  private setStoreDetail(): void {
    this.store = this.checkoutStorageService.getPickupStore();
    if (!this.store) return;
  }

  private setDeliveryAddress(): void {
    this.direccionDespacho = this.checkoutStorageService.getDeliveryAddress();
    if (!this.direccionDespacho) return;
  }

  /**
   * Al detectar un cambio en el tipo de envío,
   * se establece el shippingType y el título del tipo de envío.
   */
  private onShippingTypeChange(): void {
    this.cartService.shippingType$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shippingType) => {
        this.shippingType = shippingType;
        this.shippingTypeTitle = this.getShippingTypeTitle(this.shippingType);
      });
  }

  /**
   * Al detectar un cambio en los productos del carro, se valida si los productos...
   */
  private onShoppingCartProductsChange(): void {
    (this.cartService.items$ as Observable<IShoppingCartProduct[]>)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((products: IShoppingCartProduct[]) => {
        if (!products.length) return;
        this.validateProductAvailability(products);
      });
  }

  /**
   * Valida la disponibilidad de los productos según el tipo de envío seleccionado
   * y los clasifica en disponibles y no disponibles.
   *
   * @param products - Array de productos del carrito a validar
   *
   * Funcionamiento:
   * - Si el tipo de envío es 'retiro' o 'TIENDA':
   *   - Productos sin conflicto de retiro -> disponibles
   *   - Productos con conflicto de retiro -> no disponibles
   * - Si el tipo de envío es despacho:
   *   - Productos sin conflicto de entrega -> disponibles
   *   - Productos con conflicto de entrega -> no disponibles
   */
  private validateProductAvailability(products: IShoppingCartProduct[]): void {
    this.productsAvailable = [];
    this.productsUnavailable = [];
    if (!products.length) return;

    products.map((item) => {
      const isPickupType = ['retiro', 'TIENDA'].includes(
        this.shippingType || ''
      );

      if (isPickupType) {
        !item.pickupConflict
          ? this.productsAvailable.push(item)
          : this.productsUnavailable.push(item);
      } else {
        !item.deliveryConflict
          ? this.productsAvailable.push(item)
          : this.productsUnavailable.push(item);
      }
    });
    this.cartService.emitValidateProducts(this.productsAvailable);
  }
}
